import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Area, AreaChart, CartesianGrid, Legend, Tooltip, XAxis, YAxis } from 'recharts'
import { formatEuroFr } from '../utils/editProductOutcome'
import { formatEuroAxisCompact } from '../utils/analyticsPolicy'
import { useIsNarrow } from '../hooks/useMediaQuery'

export default function CumulativeValueChart({
  data,
  height = 280,
  colors,
  axis,
  grid,
  animate = true,
  replayToken,
  tooltip,
}) {
  const narrow = useIsNarrow(720)
  const hostRef = useRef(null)
  const playedToken = useRef(null)
  const [size, setSize] = useState({ width: 0, height })
  const [phase, setPhase] = useState('idle')
  const rows = Array.isArray(data) ? data : []

  useLayoutEffect(() => {
    const node = hostRef.current
    if (!node) return undefined
    const apply = () => {
      const rect = node.getBoundingClientRect()
      setSize({
        width: Math.floor(rect.width),
        height: Math.floor(rect.height) || height,
      })
    }
    apply()
    const observer = new ResizeObserver(apply)
    observer.observe(node)
    return () => observer.disconnect()
  }, [height])

  useEffect(() => {
    if (size.width < 40 || rows.length === 0) {
      setPhase('idle')
      return undefined
    }
    if (!animate) {
      playedToken.current = replayToken
      setPhase('reveal')
      return undefined
    }
    if (playedToken.current === replayToken) {
      setPhase('reveal')
      return undefined
    }
    playedToken.current = replayToken
    setPhase('ready')
    let frame2 = 0
    const frame1 = requestAnimationFrame(() => {
      frame2 = requestAnimationFrame(() => setPhase('reveal'))
    })
    return () => {
      cancelAnimationFrame(frame1)
      cancelAnimationFrame(frame2)
    }
  }, [animate, replayToken, rows.length, size.width])

  return (
    <div
      ref={hostRef}
      className={`cumul-chart is-${phase}${animate ? '' : ' is-static'}`}
      style={{ height: height || '100%' }}
    >
      {rows.length === 0 ? (
        <p className="analytics-empty">Aucune donnée pour cette période.</p>
      ) : size.width >= 40 ? (
        <div className="cumul-chart-motion">
          <AreaChart
            width={size.width}
            height={size.height}
            data={rows}
            margin={narrow ? { top: 8, right: 6, left: 0, bottom: 8 } : { top: 8, right: 16, left: 8, bottom: 8 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={grid} />
            <XAxis dataKey="date" stroke={axis} interval="preserveStartEnd" minTickGap={narrow ? 24 : 5} />
            <YAxis
              stroke={axis}
              tickFormatter={(value) => (narrow ? formatEuroAxisCompact(value) : formatEuroFr(value))}
              width={narrow ? 52 : 88}
              label={narrow ? undefined : { value: 'Valeur', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip content={tooltip} />
            <Legend />
            <Area
              type="monotone"
              dataKey="cumulative"
              name="Valeur cumulée actuelle"
              stroke={colors[0]}
              fill={colors[0]}
              fillOpacity={0.22}
              isAnimationActive={false}
            />
          </AreaChart>
        </div>
      ) : null}
    </div>
  )
}
